import { gql, useMutation, useQuery } from "@apollo/client";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  TextField,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { DateTime } from "luxon";
import { noElement } from "mt-shared/collections";
import { genProductPriceLabel } from "mt-shared/genProductPriceLabel";
import { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AvailabilityType,
  Currency,
  GetProductQuery,
  GoogleMapsQuery,
  Iso639,
} from "../../../__generated__/graphql";
import { useNotifications } from "../../../components/Notification";
import PickupLocation from "../../../components/PickupLocation";
import {
  PickupAnswer,
  PickupLocationType,
} from "../../../components/PickupLocation/types";
import globalContext from "../../../global-context";
import { getCloudinaryUrl } from "../../../helpers/cloudinary";
import { getGrossPrice } from "../../../helpers/get-gross-price";
import { getPrivateGroupSize } from "../../../helpers/get-private-group-size";
import { JAPAN_COUNTRY_ID } from "../../../helpers/location";
import useAvailability from "../../../hooks/useAvailability";
import AvailabilityProvider from "../../../providers/AvailabilityProvider";
import CheckoutProgress from "../components/CheckoutProgress";
import GuestNamesSummary from "../components/GuestNamesSummary";
import DurationInfo from "./DurationInfo";
import ExtraInfo from "./ExtraInfo";
import Availability from "./FormElements/Availability";
import { default as CollapsibleFormContainer } from "./FormElements/CollapsibleFormContainer";
import CustomerInfoForm, {
  FullName,
  isValidEmail,
} from "./FormElements/CustomerInfoForm";
import DietaryRestrictions from "./FormElements/DietaryRestrictions/DietaryRestrictions";
import FormElement from "./FormElements/FormElement";
import GroupTypeToggle from "./FormElements/GroupTypeToggle";
import GuestPicker from "./FormElements/GuestPicker";
import PrivatePriceTable from "./FormElements/PrivatePriceTable";
import MeetingPlaceInfo from "./MeetingPlaceInfo";
import styles from "./index.module.scss";

export type Product = GetProductQuery["product"];
export type ProductPrice = NonNullable<NonNullable<Product>["prices"]>[number];

export default function TourCheckout() {
  const context = useContext(globalContext);
  const { id } = useParams();
  const { data, loading, error } = useQuery(GET_PRODUCT, {
    variables: {
      productId: id || "",
      lang: Iso639.En,
      currency: context.currency as Currency,
    },
  });
  const product = data ? data.product : null;

  return (
    <>
      <CheckoutProgress activeStep={0} />
      {loading && <LinearProgress color="error" />}
      {error && <>ERROR Something went wrong</>}
      {!loading && product && (
        <AvailabilityProvider productId={product.id}>
          <Form product={product} />
        </AvailabilityProvider>
      )}
    </>
  );
}

function Form(props: { product: NonNullable<Product> }) {
  const product = props.product;

  const { checkTimeslotIsAvailable } = useAvailability();
  const context = useContext(globalContext);
  const navigate = useNavigate();
  const { showNotification } = useNotifications();
  const isNotSmall = useMediaQuery("(min-width:900px)");

  const [details, setDetails] = useState<PriceDetails[]>(
    initializeDetails(product),
  );
  const [reservationDate, setReservationDate] = useState("");
  const [reservationTime, setReservationTime] = useState("");
  const [availableSpots, setAvailableSpots] = useState<null | number>(null);
  const [names, setNames] = useState<FullName[]>([
    { givenName: "", familyName: "" },
  ]);
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [sourceBookingId, setSourceBookingId] = useState("");
  const [notes, setNotes] = useState("");
  const [isPrivate, setIsPrivate] = useState(
    product.availabilityType === AvailabilityType.Private,
  );
  const [pickupLocation, setPickupLocation] =
    useState<PickupLocationType>(null);
  const [pickUpAnswer, setPickUpAnswer] = useState<PickupAnswer>(
    PickupAnswer.IDK,
  );

  const hasDietaryRestrictions = useMemo(
    () => !noElement(product.dietaryRestrictions),
    [product.dietaryRestrictions],
  );

  const minPrivateGroupSize = useMemo(
    () => product.privatePrices?.[0]?.minGroupSize || 0,
    [product.privatePrices],
  );
  const maxPrivateGroupSize = useMemo(() => {
    const lastPrivatePrice =
      product.privatePrices?.[product.privatePrices.length - 1];
    return lastPrivatePrice?.price === null
      ? lastPrivatePrice.minGroupSize - 1
      : product.defaultMaximumInGroup;
  }, [product.privatePrices]);

  const isPrivateBooking = product.availabilityType === "PRIVATE" || isPrivate;

  const bookingSize = useMemo(
    () =>
      isPrivateBooking
        ? getPrivateGroupSize(details, product.availabilityType)
        : details.reduce((sum, detail) => sum + detail.quantity, 0),
    [details, product.availabilityType, isPrivate],
  );

  const maxBookingSize = useMemo(
    () =>
      isPrivateBooking
        ? maxPrivateGroupSize
        : Math.max(
            product.defaultMaximumInGroup,
            ...product.timeslots.map(
              (timeslot) => timeslot.maximumInGroup ?? 0,
            ),
          ),
    [product.defaultMaximumInGroup, product.timeslots, isPrivateBooking],
  );

  useEffect(() => {
    // reset details if they exceeded the max after toggling public/private
    if (bookingSize > maxBookingSize) setDetails(initializeDetails(product));
  }, [bookingSize, maxBookingSize, setDetails]);

  const groupPrice = useMemo(
    () =>
      isPrivate
        ? ([...(product.privatePrices || [])]
            .reverse()
            .find(
              (price) =>
                price.price !== null && bookingSize >= price.minGroupSize,
            )?.price ?? null)
        : null,
    [isPrivate, bookingSize, product.privatePrices],
  );
  const totalPrice = useMemo(
    () =>
      details.reduce(
        (sum, detail) => (sum += detail.quantity * detail.price.amount),
        0,
      ) + (groupPrice ?? 0),
    [details, groupPrice],
  );

  const isJapanProduct = useMemo(
    () => product.location.countryId === JAPAN_COUNTRY_ID,
    [product.location],
  );

  useEffect(() => {
    if (isJapanProduct) return;
    // update the length of the names array based on price details (for thai tours)
    const quantity = details.reduce((sum, { quantity }) => sum + quantity, 0);
    setNames((prevNames) => [
      ...prevNames.slice(0, quantity || 1),
      ...Array.from({ length: quantity - prevNames.length }).map(() => ({
        givenName: "",
        familyName: "",
      })),
    ]);
  }, [details, isJapanProduct]);

  const grossTotalPrice = getGrossPrice(totalPrice);

  const saveErrors = useMemo(() => {
    const errors: string[] = [];
    if (
      reservationTime &&
      availableSpots !== null &&
      availableSpots < bookingSize
    )
      errors.push("not enough spots left for selected timeslot");
    else if (
      reservationTime &&
      !checkTimeslotIsAvailable(
        reservationDate,
        reservationTime,
        bookingSize,
        isPrivate,
      )
    )
      errors.push("timeslot is not available for your selection");
    if (bookingSize === 0) errors.push("please select at least 1 guest");
    if (!(reservationDate && reservationTime))
      errors.push("please select a date and time");
    if (names.some(({ givenName, familyName }) => !(givenName && familyName))) {
      errors.push(
        names.length === 1
          ? "please enter the customer's name"
          : "please enter all customer names",
      );
    }
    if (!(email && isValidEmail(email)))
      errors.push("please enter a valid customer email");
    if (isPrivate && bookingSize < minPrivateGroupSize)
      errors.push(`group requires at least ${minPrivateGroupSize} guests`);
    if (isPrivate && bookingSize > maxPrivateGroupSize)
      errors.push(`group can have at most ${maxPrivateGroupSize} guests`);

    return errors;
  }, [
    availableSpots,
    email,
    names,
    isPrivate,
    maxPrivateGroupSize,
    minPrivateGroupSize,
    bookingSize,
    reservationDate,
    reservationTime,
    checkTimeslotIsAvailable,
  ]);

  const [createNewBooking, { loading: submitting }] = useMutation<{
    createNewBooking: { id: string };
  }>(CREATE_NEW_BOOKING);

  const formIsValid = useMemo(() => !saveErrors.length, [saveErrors]);
  const canSave = useMemo(
    () => formIsValid && !submitting,
    [formIsValid, submitting],
  );

  return (
    <Grid container spacing={4}>
      {!isNotSmall && (
        <Grid item xs={12} md={8}>
          <ProductInfo />
        </Grid>
      )}
      <Grid item xs={12} md={8}>
        <Stack spacing={2}>
          <FormElement title="Guests">
            <GuestPicker
              prices={product.prices || []}
              details={details}
              setDetails={setDetails}
              bookingSize={bookingSize}
              maxBookingSize={maxBookingSize}
            />
          </FormElement>
          {product.availabilityType === AvailabilityType.PublicAndPrivate && (
            <FormElement title="Group Type">
              <GroupTypeToggle
                isPrivate={isPrivate}
                setIsPrivate={setIsPrivate}
              />
            </FormElement>
          )}
          {isPrivate && (
            <FormElement title="Group Prices">
              <PrivatePriceTable
                privatePrices={product.privatePrices || []}
                defaultMaximumInGroup={product.defaultMaximumInGroup}
              />
            </FormElement>
          )}

          <FormElement title={"Date & Time"}>
            <Availability
              productId={product.id}
              reservationDate={reservationDate}
              setReservationDate={setReservationDate}
              reservationTime={reservationTime}
              setReservationTime={setReservationTime}
              setAvailableSpots={setAvailableSpots}
              numSelectedPeople={bookingSize}
              isPrivate={isPrivate}
            />
          </FormElement>
          <FormElement title="Customer Info">
            <CustomerInfoForm
              email={email}
              names={names}
              phoneNumber={phoneNumber}
              sourceBookingId={sourceBookingId}
              setEmail={setEmail}
              setNames={setNames}
              setPhoneNumber={setPhoneNumber}
              setSourceBookingId={setSourceBookingId}
            />
          </FormElement>
          {product.hasAccomodationVehiclePickup && (
            <FormElement title="Pickup Location">
              <PickupLocation
                pickupLocationZone={
                  product.pickupLocationZone ?? {
                    center: { lat: 0, lng: 0 },
                    radius: 0,
                  }
                }
                setPickupLocation={setPickupLocation}
                pickupLocation={pickupLocation}
                defaultMeetingPointQuery={
                  product.meetingPlaceGoogleMapsQuery ??
                  ({
                    id: "",
                    created: new Date().toISOString(),
                    url: "",
                    placeId: "",
                    geoLocation: { lat: 0, lng: 0 },
                  } as GoogleMapsQuery)
                }
                defaultMeetingPlaceName={
                  product.translation?.meetingPlaceName ?? ""
                }
                hasDefaultVehiclePickup={product.hasDefaultVehiclePickup}
                hasAccomodationVehiclePickup={
                  product.hasAccomodationVehiclePickup
                }
                pickUpAnswer={pickUpAnswer}
                setPickUpAnswer={setPickUpAnswer}
                action="confirm"
              />
            </FormElement>
          )}
          <FormElement title="Notes">
            <TextField
              sx={{ width: "100%", backgroundColor: "white" }}
              multiline
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any information that needs to be known for this customer (optional)"
            />
          </FormElement>

          {hasDietaryRestrictions && (
            <FormElement
              title="More Info"
              contentGridProps={{ position: "relative", top: "-20px" }}
            >
              <CollapsibleFormContainer title={"Dietary Restrictions"}>
                <DietaryRestrictions
                  dietaryRestrictions={product.dietaryRestrictions}
                />
              </CollapsibleFormContainer>
            </FormElement>
          )}
        </Stack>
      </Grid>
      <Grid item xs={12} md={4}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: "white",
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            p: 3,
          }}
        >
          {isNotSmall && <ProductInfo />}
          {isNotSmall && (
            <Box sx={{ mt: "20px", mb: "20px" }}>
              <Divider />
            </Box>
          )}
          {!isNotSmall && (
            <div
              style={{
                fontSize: "18px",
                fontWeight: "bold",
                textAlign: "center",
              }}
            >
              Summary
            </div>
          )}
          <div style={{ marginTop: "10px" }}>
            <GuestNamesSummary names={names} />
            {email && (
              <div>
                <b>Email:</b> {email}
              </div>
            )}
            {reservationDate && (
              <div>
                <b>Reservation Date / Time:</b> {reservationDate}{" "}
                {reservationTime} (
                {DateTime.fromFormat(product.location?.timezone || "", "z", {
                  zone: product.location?.timezone || "",
                }).toFormat("ZZZZ")}
                )
              </div>
            )}
            {bookingSize > 0 && (
              <div>
                <b>Guests:</b>{" "}
                {details.map((detail) => {
                  if (detail.quantity > 0) {
                    return (
                      <Stack
                        key={detail.price.id}
                        direction="row"
                        justifyContent="space-between"
                      >
                        <div>
                          {detail.quantity} x{" "}
                          {genProductPriceLabel(detail.price)}
                        </div>
                        {product.availabilityType !== "PRIVATE" && (
                          <div>
                            {Intl.NumberFormat("ja-JP", {
                              style: "currency",
                              currency: context.currency,
                            }).format(detail.quantity * detail.price.amount)}
                          </div>
                        )}
                      </Stack>
                    );
                  }
                })}
                {groupPrice !== null && (
                  <Stack direction="row" justifyContent="space-between">
                    <b>Group:</b>
                    {Intl.NumberFormat("ja-JP", {
                      style: "currency",
                      currency: context.currency,
                    }).format(groupPrice)}
                  </Stack>
                )}
                <div className={styles.priceSummary}>
                  {grossTotalPrice && (
                    <div className={styles.grossPrice}>
                      (Gross Price:{" "}
                      {Intl.NumberFormat("ja-JP", {
                        style: "currency",
                        currency: context.currency,
                      }).format(grossTotalPrice)}
                      )
                    </div>
                  )}
                  <div className={styles.netPrice}>
                    <span className={styles.netPriceLabel}>Net Price:</span>
                    <span className={styles.netPriceAmount}>
                      {Intl.NumberFormat("ja-JP", {
                        style: "currency",
                        currency: context.currency,
                      }).format(totalPrice)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className={styles.checkoutActions}>
            {saveErrors.length > 0 && (
              <div className={styles.errorSummary}>
                {saveErrors.map((error) => (
                  <div key={error}>{error}</div>
                ))}
              </div>
            )}
            <Button
              className={styles.nextButton}
              variant="contained"
              color="error"
              disabled={!canSave}
              onClick={handleSubmit}
            >
              {submitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "next"
              )}
            </Button>
          </div>
        </Paper>
      </Grid>
    </Grid>
  );

  function ProductInfo() {
    const [videoLoaded, setVideoLoaded] = useState(false);
    const imageUrl = product.mainImage
      ? getCloudinaryUrl("image", product.mainImage, { height: 300 })
      : "";
    const videoUrl = product.mainVideo
      ? getCloudinaryUrl("video", product.mainVideo, { height: 300 })
      : "";

    return (
      <>
        <div
          style={{
            fontSize: "18px",
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          {product.translation?.name}
        </div>
        {(product.mainImage || product.mainVideo) && (
          <Paper
            sx={{
              mt: "20px",
              height: 250,
              backgroundImage: imageUrl ? `url(${imageUrl})` : undefined,
              backgroundSize: "cover",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              overflow: "hidden",
              position: "relative",
            }}
          >
            {videoUrl && (
              <Box
                component="video"
                src={videoUrl}
                poster={imageUrl || undefined}
                muted
                loop
                autoPlay
                playsInline
                preload="metadata"
                onLoadedData={() => setVideoLoaded(true)}
                sx={{
                  display: "block",
                  height: "100%",
                  width: "100%",
                  objectFit: "cover",
                  opacity: videoLoaded || !imageUrl ? 1 : 0,
                  transition: "opacity 200ms ease",
                }}
              />
            )}
          </Paper>
        )}
        <Stack
          direction="row"
          spacing={3}
          sx={{ mt: "16px" }}
          alignItems="flex-start"
        >
          <DurationInfo durationInMinutes={product.duration} />
          <MeetingPlaceInfo
            meetingPlaceName={product.translation?.meetingPlaceName}
            meetingPlaceGoogleMapsQuery={product.meetingPlaceGoogleMapsQuery}
          />
        </Stack>
        <ExtraInfo
          highlights={product.translation?.highlights}
          inclusions={
            product.inclusions?.map((i) => ({
              index: i.index,
              title: i.translation?.title || "",
              description: i.translation?.description || "",
              icon: i.icon || "",
            })) ?? []
          }
          itinerary={product.translation?.itinerary}
          remarks={product.translation?.remarks}
        />
        <Box sx={{ width: "100%", textAlign: "center", mt: "12px", mb: "0px" }}>
          <Button
            className={styles.mtWebsiteButton}
            component={Link}
            to={`https://www.magical-trip.com/product/${product.id}`}
            target="_blank"
            size="small"
            variant="outlined"
            endIcon={<OpenInNewIcon />}
          >
            View tour on MT website
          </Button>
        </Box>
      </>
    );
  }

  function handleSubmit() {
    createNewBooking({
      variables: {
        input: {
          productId: product.id,
          details: details.map((detail) => ({
            priceTypeId: detail.price.id,
            quantity: detail.quantity,
          })),
          date: reservationDate,
          time: reservationTime,
          currency: context.currency,
          guests: names.map(({ givenName, familyName }, index) => ({
            givenName: givenName.trim(),
            familyName: familyName.trim(),
            index,
          })),
          customerEmail: email,
          customerPhoneNumber: phoneNumber,
          sourceBookingId: sourceBookingId,
          notes: notes,
          isPrivate: isPrivate,
          pickupLocation: pickupLocation,
        },
      },
    }).then(
      ({ data }) => {
        navigate(`confirmation?bookingid=${data?.createNewBooking.id}`);
      },
      () => {
        showNotification({
          severity: "error",
          message: "Failed to create new booking",
        });
      },
    );
  }
}

const GET_PRODUCT = gql`
  query GetProduct($productId: ID!, $lang: Iso639!, $currency: Currency!) {
    product(id: $productId) {
      id
      availabilityType
      defaultMaximumInGroup
      duration
      mainImage
      mainVideo
      dietaryRestrictions {
        diet
        isPossible
        memo
      }
      location {
        timezone
        countryId
      }
      timeslots {
        dayOfWeek
        timeslot
        maximumInGroup
      }
      translation(lang: $lang) {
        name
        highlights
        included
        itinerary
        meetingPlaceName
        remarks
      }
      prices(currency: $currency) {
        id
        amount
        currency
        minimum
        minAge
        maxAge
        tags
        excludeFromPrivateGroupCount
        translation(lang: $lang) {
          description
        }
      }
      privatePrices(currency: $currency) {
        id
        minGroupSize
        price
      }
      inclusions {
        id
        index
        icon
        translation(lang: $lang) {
          lang
          title
          description
        }
      }
      meetingPlaceGoogleMapsQuery {
        id
        lastUpdated
        url
        placeId
        geoLocation {
          lat
          lng
        }
      }
      hasAccomodationVehiclePickup
      hasDefaultVehiclePickup
      pickupLocationZone {
        center {
          lat
          lng
        }
        radius
      }
    }
  }
`;

const CREATE_NEW_BOOKING = gql`
  mutation CreateNewBooking($input: CreateNewBookingInput!) {
    createNewBooking(input: $input) {
      id
    }
  }
`;

export type PriceDetails = {
  price: ProductPrice;
  quantity: number;
};

const initializeDetails = (product: Product) =>
  (product?.prices ?? []).map((price) => ({
    price: price,
    quantity: price.minimum,
    excludeFromPrivateGroupCount: price.excludeFromPrivateGroupCount,
  }));
